import styled, { css } from 'styled-components';
import {
    FloatingArrow,
    useFloating,
    arrow,
    useInteractions,
    useHover,
    safePolygon,
    offset,
    autoPlacement,
    autoUpdate,
    shift,
    type UseFloatingOptions,
    FloatingPortal,
    hide
} from '@floating-ui/react';
import React, { type HTMLAttributes, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import Icon from 'components/Icon';
import useTooltipStore from './hooks/useTooltipStore';
import { Typography } from 'components/generic/typography';
import type { IBody } from 'components/generic/typography/components/Body';
import { useDynamicStorage } from 'hooks/storage';
import { zIndex } from 'variables';
import useHasInformationCookieBeenAccepted from 'hooks/user/useHasInformationCookieBeenAccepted';
import { CookieCategory } from 'modules/helpers/cookieInformationHelper';

type ActionProps = {
    close: () => void;
};

type ContentRendererObj = {
    headline?: string;
    body: string;
    action?: (props: ActionProps) => ReactNode;
};

type TooltipProps = Omit<HTMLAttributes<HTMLDivElement>, 'children'> & {
    id?: string;
    type?: 'primary' | 'secondary';
    size?: 'small' | 'large';
    enabled?: boolean;
    enableHover?: boolean;
    popup?: boolean;
    popupDelay?: number;
    popupDuration?: number;
    popupMaxCount?: number;
    popupCountStorage?: 'localStorage' | 'sessionStorage';
    children: (props: Record<string, unknown>) => ReactNode;
    contentRenderer: ((props: ActionProps) => ReactNode) | ContentRendererObj;
    floatingOptions?: UseFloatingOptions;
};

const setSize = ({ $size }: { $size: TooltipProps['size'] }) => {
    return {
        size: $size === 'small' ? 'S' : ('M' as IBody['size'])
    };
};

const Headline = styled(Typography.Body).attrs<{ $size: TooltipProps['size'] }>(setSize)`
    font-weight: bold;
`;

const Body = styled(Typography.Body).attrs<{ $size: TooltipProps['size'] }>(setSize)``;

const LinkActionBody = styled(Body)`
    margin-top: ${({ theme }) => theme.Spacing8};
`;

const vwSpacing = 30;

const TooltipBody = styled.div<{ $type: TooltipProps['type']; $showCloseButton: boolean }>`
    z-index: ${zIndex.fixed};
    background-color: ${({ theme, $type }) => ($type === 'primary' ? theme.ColorBackgroundBrand : theme.ColorBackgroundNeutral)};
    padding: ${({ theme }) => `${theme.Spacing12} ${theme.Spacing16}`};
    border-radius: ${({ theme }) => theme.RadiusMedium};
    max-width: calc(100vw - ${vwSpacing * 2}px);
    transition: opacity 300ms ease;

    ${({ $showCloseButton }) =>
        $showCloseButton &&
        css`
            padding-right: ${({ theme }) => theme.Spacing40};
        `}

        &, ${Headline}, ${Body}, ${LinkActionBody} * {
            color: ${({ theme, $type }) => ($type === 'primary' ? theme.ColorTextPrimaryInverted : theme.ColorTextPrimary)};
        }
`;

const StyledFloatingArrow = styled(FloatingArrow)<{ $type: TooltipProps['type'] }>`
    position: absolute;
    fill: ${({ theme, $type }) => ($type === 'primary' ? theme.ColorBackgroundBrand : theme.ColorBackgroundNeutral)};
`;

const StyledCloseIcon = styled(Icon)<{ $type: TooltipProps['type'] }>`
    display: block;
    fill: ${({ theme, $type }) => ($type === 'primary' ? theme.ColorIconPrimaryInverted : theme.ColorIconPrimary)};
`;

const CloseButton = styled.button`
    position: absolute;
    top: 14px;
    right: 10px;
    background: none;
    border: 0;
`;

export default function Tooltip(props: TooltipProps) {
    const autoId = useId();
    const {
        children,
        id = autoId,
        type = 'primary',
        size = 'small',
        contentRenderer,
        floatingOptions,
        enabled = true,
        enableHover = true,
        popup = false,
        popupDelay = 1500,
        popupDuration = 15000,
        popupMaxCount = 3,
        popupCountStorage = 'localStorage',
        ...restProps
    } = props;
    const setOpen = useTooltipStore((state) => state.setOpen);
    const { isOpen } = useTooltipStore((state) => state.tooltips[id]) || {};
    const arrowRef = useRef(null);
    const arrowSizeProps = size === 'small' ? { width: 16, height: 8 } : { width: 24, height: 12 };
    const [popupCount, setPopupCount] = useDynamicStorage(popupCountStorage, `tooltip-${id}-popupCount`, 0);
    const [showCloseButton, setShowCloseButton] = useState(false);
    const isCookieAccepted = useHasInformationCookieBeenAccepted(CookieCategory.functional);

    const { floatingStyles, refs, context, middlewareData } = useFloating({
        open: isOpen,
        onOpenChange: (open) => {
            setOpen(id, open);
            setShowCloseButton(false);
        },
        middleware: [
            offset(arrowSizeProps.height),
            autoPlacement({ allowedPlacements: ['top', 'bottom'] }),
            shift({ padding: vwSpacing }),
            arrow({ element: arrowRef }),
            hide({ strategy: 'referenceHidden' })
        ],
        whileElementsMounted: autoUpdate,
        ...floatingOptions
    });

    const hover = useHover(context, {
        delay: 100,
        handleClose: safePolygon(),
        enabled: enableHover
    });

    const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

    function userClose() {
        setOpen(id, false);

        // If user dismisses the Tooltip when it pops up, we ensure that the tooltip is not shown again
        if (popup) {
            setPopupCount(popupMaxCount);
        }
    }

    // Popup logic
    useEffect(() => {
        if (popup && popupCount < popupMaxCount && isCookieAccepted && enabled) {
            const delayTimer = setTimeout(() => {
                setOpen(id, true);
                setShowCloseButton(true);
                setPopupCount(popupCount + 1);
            }, popupDelay);

            const durationTimer = setTimeout(() => {
                setOpen(id, false);
            }, popupDuration);

            return () => {
                clearTimeout(delayTimer);
                clearTimeout(durationTimer);
            };
        }
    }, [isCookieAccepted, enabled]);

    if (!enabled) {
        return children({});
    }

    return (
        <>
            {children({ ref: refs.setReference, ...getReferenceProps() })}
            {isOpen && (
                <FloatingPortal>
                    <TooltipBody
                        $type={type}
                        $showCloseButton={showCloseButton}
                        ref={refs.setFloating}
                        style={{
                            ...floatingStyles,
                            opacity: middlewareData.hide?.referenceHidden ? 0 : 1
                        }}
                        {...getFloatingProps()}
                        {...restProps}
                    >
                        <StyledFloatingArrow
                            ref={arrowRef}
                            context={context}
                            $type={type}
                            {...arrowSizeProps}
                            style={{
                                left: middlewareData.arrow?.x,
                                top: middlewareData.arrow?.y
                            }}
                        />
                        {showCloseButton && (
                            <CloseButton onClick={() => setOpen(id, false)}>
                                <StyledCloseIcon type="Close" size={16} $type={type} />
                            </CloseButton>
                        )}
                        {typeof contentRenderer === 'function' ? (
                            contentRenderer({ close: userClose })
                        ) : (
                            <>
                                {contentRenderer.headline && (
                                    <Headline $size={size}>{contentRenderer.headline}</Headline>
                                )}
                                <Body $size={size}>{contentRenderer.body}</Body>
                                {contentRenderer.action && (
                                    <LinkActionBody $size={size}>
                                        {contentRenderer.action({ close: userClose })}
                                    </LinkActionBody>
                                )}
                            </>
                        )}
                    </TooltipBody>
                </FloatingPortal>
            )}
        </>
    );
}
